import React, { ChangeEvent, useEffect, useState, useRef } from 'react';

import clsx from 'clsx';

import { ClearIcon, RefreshIcon } from 'components/common/Icons/Icons';
import { KeyboardsEnums } from 'common/enums/keyboards.enums';
import Spinner from 'components/common/Spinner/Spinner';
import TextInput from 'components/common/TextInput/TextInput';
import ResendCodeTimer from 'components/auth/EnterConfirmationCodePage/ResendCodeTimer';

import styles from './ConfirmationCode.module.scss';

interface ConfirmationCodeTypeProps {
  isLoading?: boolean;
  isSuccessCode?: boolean | null;
  sendVerificationCode: () => void;
  verifyVerificationCode: (code: string) => void;
  codeLength: number;
}

const ConfirmationCode = ({
  isLoading,
  sendVerificationCode,
  verifyVerificationCode,
  codeLength,
  isSuccessCode,
}: ConfirmationCodeTypeProps) => {
  const [isRunningTimer, setIsRunningTimer] = useState<boolean>(true);
  const [code, setCode] = useState<string[]>(Array(codeLength).fill(''));

  const onSendVerificationCode = async () => {
    sendVerificationCode();
    setIsRunningTimer(true);
    setCode(Array(codeLength).fill(''));
  };

  const onVerifyVerificationCode = async () => {
    if (code.every((element) => element !== '')) {
      verifyVerificationCode(code.join(''));
    }
  };

  useEffect(() => {
    onVerifyVerificationCode();
  }, [code]);

  const resendCodeElement = isRunningTimer ? (
    <ResendCodeTimer onExpire={() => setIsRunningTimer(false)} />
  ) : (
    <div className={styles.refreshButtonWrapper} onClick={onSendVerificationCode}>
      <p className={styles.textButton}>Click here to resend code </p>
      <RefreshIcon />
    </div>
  );
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const changeActiveInput = (index: number) => {
    inputRefs.current[index].focus();
    inputRefs.current[index].select();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const values = event.clipboardData.getData('text/plain').split('');

    setCode(values);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const index = +(event.target.dataset.index as string);

    const { value } = event.target;

    if (/\d/.test(value)) {
      setCode(code.map((codeValue, codeIndex) => (codeIndex === index ? value : codeValue)));

      if (index < code.length - 1 && value) {
        changeActiveInput(index + 1);
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();

    const index = +(event.target.dataset.index as string);

    inputRefs.current[index].select();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    const index = +(target.dataset.index as string);

    switch (event.key) {
      case KeyboardsEnums.Backspace: {
        event.preventDefault();

        if (target.value !== '') {
          setCode(code.map((codeValue, codeIndex) => (codeIndex === index ? '' : codeValue)));
        } else if (index > 0) {
          setCode(code.map((codeValue, codeIndex) => (codeIndex === index - 1 ? '' : codeValue)));
          changeActiveInput(index - 1);
        }

        break;
      }
      case KeyboardsEnums.Delete: {
        event.preventDefault();

        if (index + 1 < code.length) {
          if (code[index + 1] !== '') {
            setCode(code.map((codeValue, codeIndex) => (codeIndex === index + 1 ? '' : codeValue)));
          }

          changeActiveInput(index + 1);
        }

        break;
      }
      case KeyboardsEnums.ArrowLeft: {
        event.preventDefault();

        if (index > 0) {
          changeActiveInput(index - 1);
        }

        break;
      }
      case KeyboardsEnums.ArrowRight: {
        event.preventDefault();

        if (index + 1 < code.length) {
          changeActiveInput(index + 1);
        }

        break;
      }
      default: {
        break;
      }
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <div className={styles.main}>
      <div className={styles.codeInputsWrapper}>
        <div className={styles.codeInputs} onPaste={handlePaste} onKeyDown={handleKeyDown} onFocus={handleFocus}>
          {code.map((value, index) => {
            return (
              <TextInput
                className={clsx(styles.codeInput, {
                  [styles.errorInput]: isSuccessCode === false,
                })}
                key={index}
                data-index={index.toString()}
                maxLength={1}
                onChange={handleChange}
                inputRef={(input: HTMLInputElement) => (inputRefs.current[index] = input)}
                inputMode={'numeric'}
                value={value || ''}
              />
            );
          })}
        </div>
        {isSuccessCode === false && (
          <div className={styles.errorMessage}>
            Invalid Code <ClearIcon />
          </div>
        )}
      </div>
      {resendCodeElement}
    </div>
  );
};

export default ConfirmationCode;
